import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { ISessionAuth, IPaymentForm, IPaymentFormHooks, IStateConfig, TokenizedPaymentCard, FormValidationResultData, FormValidationResult, SubmitOrderRequestPaymentMethodType } from '@/models';
import { ui } from '@/constants';
import { AccountManagementEvent, ErrorEvent } from '@/services/analytics/events';
import { InteractionTrackingType } from '@/services/analytics/platforms/adobe-analytics/models';

@Component({
  name: 'CmgPayeezyForm',
})
export default class CmgPayeezyForm extends Vue {
  @Prop({ required: true, type: String })
  providerId!: string;

  @Prop({ required: true, type: Boolean })
  checkout!: boolean;

  @Prop({ required: true, type: Boolean })
  public oneTimePayment!: boolean;

  validationWatcher?: () => void;

  errorMessage: string = '';
  ccNumberValidMessage: string = '';
  ccvValidMessage: string = '';
  ccExpValidMessage: string = '';
  ccZipCodeValidMessage: string = '';

  isSubmitted: boolean = false;
  isFormValid: boolean = true;
  isPaymentProviderInitializing = false;

  submitText: string = 'Add card';
  sessionAuth?: ISessionAuth = undefined;

  isCCNumberValid: boolean = true;
  isCCVValid: boolean = true;
  isCCExpirationDateValid: boolean = true;
  isCCZipCodeValid: boolean = true;
  hasCardholderName: boolean = true;

  private paymentForm?: IPaymentForm;
  private configPayeezy: IStateConfig = {
    fields: {
      card: {
        selector: '[data-cc-card]',
        placeholder: 'Credit Card Number',
        validity: undefined,
      },
      cvv: {
        selector: '[data-cc-cvv]',
        placeholder: 'CVV',
        validity: undefined,
      },
      exp: {
        selector: '[data-cc-exp]',
        placeholder: 'MM/YY',
        validity: undefined,
      },
      postalCode: {
        selector: '[data-cc-zip]',
        placeholder: 'Zip',
      },
      name: {
        selector: '[data-cc-name]',
        placeholder: 'Cardholder Name',
        validity: undefined,
      },
    },

    styles: {
      input: {
        'font-family': 'Nunito,Helvetica Neue,Arial,sans-serif,Apple Color Emoji,Segoe UI Emoji,Segoe UI Symbol',
        'font-size': '0.85em',
      },
      '.focus': {},

      '.invalid': {
        color: '#C01324',
      },

      '.valid': {
        color: '#000000',
      },
      'input::placeholder': {
        color: ui.colors.brown,
      },
    },

    classes: {
      empty: 'empty',
      focus: 'focus',
      invalid: 'invalid',
      valid: 'valid',
    },
  };

  public async mounted(): Promise<void> {
    this.isPaymentProviderInitializing = true;

    try {
      await this.$services.payment.loadPayeezyJsLibrary().then(async () => {
        const win = window as any;
        const firstdata = win.firstdata;

        this.$nextTick(() => this.createForm(firstdata));
      });
    } catch (e) {
      this.$services.logger.error('Could not load Payeezy script', e);
    } finally {
      this.isPaymentProviderInitializing = false;
    }

    this.validationWatcher = this.$appStore.watch(
      state => state.customer.validationResult,
      async (validationResult?: FormValidationResult) => {
        this.resetValidationState()

        if (validationResult && validationResult.error) {
          this.processClientSideValidation(validationResult.data);
        }
      },
    );
  }

  destroyed() {
    if (this.validationWatcher) {
      this.validationWatcher();
    }
  }

  public async saveCreditCard(e: Event): Promise<void> {
    e.preventDefault();

    this.isSubmitted = true;
    this.submitText = '';
    this.resetValidationState()

    try {
      if (!this.paymentForm) {
        this.$services.logger.error('The paymentForm object has not been intialized');
        this.closeModal();
        return this.$services.ui.setModalVisibility('globalErrorToast', true);
      }

      // 1. Validate form
      const valid = await this.$services.transaction.validatePaymentForm(this.paymentForm);

      setTimeout(() => {
        //wait 15 secs for a validation response, if none reset the submit button
        if (valid.data && valid.data.length === 0) {
          this.isSubmitted = false;
          this.submitText = 'Add Card';
          this.isFormValid = false;
          this.errorMessage = 'We encountered an error saving your card. Please check your card info and try again.';
        }
      }, 15000);

      if (!valid.error) {
        if (this.providerId) {
          // 2. Authenticate the current token.
          const token = await this.$services.transaction.getSessionToken(this.providerId);

          //3. Submit the form to Payeezy to tokanize it
          const clientToken = await this.$services.transaction.tokenizePayeezyCard(token, this.paymentForm);

          try {
            //4. Get the token from TransactionV3
            const tokenizedCardResponse = await this.$services.transaction.getTransactionToken(this.providerId, clientToken);
            
            const tokenizedCard = {
              cardHolderName: tokenizedCardResponse.cardHolderName,
              expiration: tokenizedCardResponse.expiration,
              paymentMethod: tokenizedCardResponse.paymentMethod!.replace(/-/g, ' '),
              tokenizedAccountNumber: tokenizedCardResponse.paymentToken,
              paymentProviderId: this.providerId,
              lastFourAccountNumbers: tokenizedCardResponse.last4,
              billingZip: tokenizedCardResponse.billingZip
            } as TokenizedPaymentCard;

            try {
              if (this.oneTimePayment) {
                this.setOneTimePaymentMethod(tokenizedCard);
              } else {
                await this.$services.transaction.savePaymentCard(tokenizedCard);
                this.$services.transaction.refreshPaymentCards();
              }
              this.closeModal();
            } catch (e) {
              this.errorMessage = 'Could not save credit card. Please try again later.';
              this.isFormValid = false;
            }
          } catch (tokenError) {
            this.$services.logger.error('An error was encountered while retrieving the tokenized card', tokenError);
            this.isFormValid = false;
            this.errorMessage = 'We encountered an error saving your card. Please check your card info and try again.';
          }
        }
      } else {
        if (valid.message) {
          this.$services.logger.error(`An error was encountered while validating the form: ${valid.message}`);
          this.$services.ui.focusDiv('.content-container');
        }
      }
    } catch (error) {
      this.$services.logger.error('An error was encountered while submitting the form', error);
      this.isFormValid = false;
      this.closeModal();
      this.$services.ui.setModalVisibility('globalErrorToast', true);
    } finally {
      this.isSubmitted = false;
      this.submitText = 'Add Card';
    }

    this.$services.analytics.send(new AccountManagementEvent(InteractionTrackingType.AddCreditCard));
  }

  private setOneTimePaymentMethod(tokenizedCard: TokenizedPaymentCard) {
    // Mark card as selected
    this.$services.checkout.setPaymentMethods([
      {
        cardHolderName: tokenizedCard.cardHolderName,
        creditCardSingleUseToken: tokenizedCard.tokenizedAccountNumber,
        creditCardType: tokenizedCard.paymentMethod.replace(/-/g, ' '),
        creditCardExpiration: tokenizedCard.expiration,
        creditCardZipCode: tokenizedCard.billingZip,
        paymentType: SubmitOrderRequestPaymentMethodType.CreditCard,
        paymentProviderId: this.providerId,
        lastFourAccountNumbers: tokenizedCard.lastFourAccountNumbers,
      },
    ]);

    // Include card in card list as one-time-payment
    if (tokenizedCard.expiration) {
      tokenizedCard.expirationMonth = tokenizedCard.expiration.substr(0, 2);
      tokenizedCard.expirationYear = tokenizedCard.expiration.substr(2, 2);
    }
    tokenizedCard.paymentMethod = tokenizedCard.paymentMethod.toLowerCase();
    tokenizedCard.isOneTimePaymentCard = true;
    this.$services.checkout.setOneTimePayCard(tokenizedCard);
  }

  private processClientSideValidation(data: FormValidationResultData[] | undefined): void {
    this.isFormValid = false;
    if (data && this.paymentForm) {
      for (const item of data) {
        switch (item.field) {
          case 'card':
            if (item.empty) {
              this.isCCNumberValid = false;
              this.ccNumberValidMessage = 'Credit Card Number Required';
            } else if (!item.valid) {
              this.isCCNumberValid = false;
              this.ccNumberValidMessage = 'Invalid Credit Card Number';
            }
            this.$services.analytics.send(new ErrorEvent('payment', this.ccNumberValidMessage, 'payeezy-form'));
            break;
          case 'cvv':
            if (item.empty) {
              this.isCCVValid = false;
              this.ccvValidMessage = 'CVV Required';
            } else if ((item.length !== 3 && !this.isAmexCard()) || (item.length !== 4 && this.isAmexCard())) {
              this.isCCVValid = false;
              this.ccvValidMessage = 'Invalid CVV';
            }
            this.$services.analytics.send(new ErrorEvent('payment', this.ccvValidMessage, 'payeezy-form'));
            break;
          case 'exp':
            if (item.empty) {
              this.isCCExpirationDateValid = false;
              this.ccExpValidMessage = 'Expiration Date Required';
            } else if (!item.valid) {
              this.isCCExpirationDateValid = false;
              this.ccExpValidMessage = 'Invalid Expiration Date';
            }
            this.$services.analytics.send(new ErrorEvent('payment', this.ccExpValidMessage, 'payeezy-form'));
            break;
          case 'postalCode':
            if (item.empty) {
              this.isCCZipCodeValid = false;
              this.ccZipCodeValidMessage = 'Zip Required'
            } else if (!item.valid) {
              this.isCCZipCodeValid = false;
              this.ccZipCodeValidMessage = 'Invalid Zip';
            }
            this.$services.analytics.send(new ErrorEvent('payment', this.ccZipCodeValidMessage, 'payeezy-form'));
            break;
          case 'name':
            if (item.empty) {
              this.hasCardholderName = false;
              this.$services.analytics.send(new ErrorEvent('payment', 'Cardholder Name Required', 'payeezy-form'));
            }
            break;
        }
      }
    }
  }

  private isAmexCard(): boolean | undefined {
    return this.paymentForm && this.paymentForm.state.fields.card.validity && this.paymentForm.state.fields.card.validity.brand == 'american-express';
  }

  private readonly paymentLogger = (level: string, message: string) => {
    switch (level) {
      case 'error':
        this.$services.logger.error(message);
        break;
      case 'warn':
        this.$services.logger.warn(message);
        break;
      case 'info':
        this.$services.logger.info(message);
        break;
      case 'debug':
      default:
        this.$services.logger.debug(message);
        break;
    }
  }

  private async sessionToken(): Promise<ISessionAuth> {
    if (!this.sessionAuth) {
      try {
        this.sessionAuth = await this.$services.transaction.getSessionToken();
      } catch (error) {
        this.$services.logger.error('An error was encountered while retrieving a Payeezy session authorization token from the server');
        this.closeModal();
        this.$services.ui.setModalVisibility('globalErrorToast', true);
      }
    }
    return this.sessionAuth!;
  }

  private async paymentFormCallback(paymentForm: IPaymentForm): Promise<void> {
    this.isPaymentProviderInitializing = false;

    // Initialize the form's events and such.
    this.paymentForm = paymentForm;

    //if on checkout page add the form to the state
    if (this.checkout) {
      this.$services.checkout.setPaymentForm(paymentForm);
    }
  }

  private paymentFormHooks(): IPaymentFormHooks {
    return { preFlowHook: () => this.sessionToken() };
  }

  private async createForm(firstdata: any): Promise<void> {
    try {
      firstdata.createPaymentForm(this.configPayeezy, this.paymentFormHooks(), (paymentForm: IPaymentForm) => this.paymentFormCallback(paymentForm), this.paymentLogger);
    } catch (error) {
      // Prevent logging an error that Payeezy throws when nothing is wrong on hard page refresh, intermittently on soft page refresh
      if (error.message !== '[data-cc-card] is not a valid field selector.') {
        this.$services.logger.error('An error was encountered while creating the form', error);
      }
      this.closeModal();
    }
  }

  private closeModal(): void {
    const args = this.$appStore.state.ui.modals.addCreditOrDebitCardModal.args;
    if (args && args.onClose) {
      args.onClose();
    }
    this.$services.ui.setModalVisibility('addCreditOrDebitCardModal', false);
    const focusNearby = document.getElementsByClassName('pay-total')[0] as HTMLElement;
    if (focusNearby) {
      focusNearby.focus();
    }
    Vue.nextTick(() => {
      setTimeout(() => {
        const focusaddAnothercard = document.getElementsByClassName('addAnothercard')[0] as HTMLElement;
        if (focusaddAnothercard) {
          focusaddAnothercard.focus();
          this.$services.ui.clearLastSelectedElement();
        } else {
          const focusaddGiftcard = document.getElementsByClassName('gift-card-section-header')[0] as HTMLElement;
          if(focusaddGiftcard){
            focusaddGiftcard.focus();
            this.$services.ui.clearLastSelectedElement();
          }
        }
      }, 2000);
    });
  }

  private resetValidationState(): void {
    this.isCCExpirationDateValid = true;
    this.isCCNumberValid = true;
    this.isCCVValid = true;
    this.isCCZipCodeValid = true;
    this.hasCardholderName = true;
    this.ccExpValidMessage = '';
    this.ccNumberValidMessage = '';
    this.ccvValidMessage = '';
    this.ccZipCodeValidMessage = '';
    this.isFormValid = true;
    this.errorMessage = '';
  }
}
